import React, { useState, useRef } from 'react';
import { X, Save, ImageIcon, Trash2 } from 'lucide-react';
import { useToast } from './Toast';
interface NoteModalProps {
  questionId: number;
  initialNotes: string;
  initialImages?: string[];
  onClose: () => void;
  onSave: (id: number, notes: string, images: string[]) => void;
  title: string;
}
export function NoteModal({
  questionId,
  initialNotes,
  initialImages = [],
  onClose,
  onSave,
  title
}: NoteModalProps) {
  const [notes, setNotes] = useState(initialNotes);
  const [noteImages, setNoteImages] = useState<string[]>(initialImages);
  const [isDirty, setIsDirty] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    showToast,
    ToastContainer
  } = useToast();
  const handleSaveNotes = () => {
    onSave(questionId, notes, noteImages);
    setIsDirty(false);
    showToast('Notlarınız kaydedildi', 'success');
    onClose();
  };
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    setIsDirty(true);
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        showToast("Dosya boyutu 5MB'dan küçük olmalıdır", 'error');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setNoteImages([...noteImages, base64String]);
        setIsDirty(true);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveImage = (index: number) => {
    setNoteImages(noteImages.filter((_, i) => i !== index));
    setIsDirty(true);
  };
  // Handle click outside to close modal
  const modalContentRef = useRef<HTMLDivElement>(null);
  const handleModalClick = (e: React.MouseEvent) => {
    if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
      onClose();
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75" onClick={handleModalClick} data-id="element-798">
      <div ref={modalContentRef} className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-lg mx-4" onClick={e => e.stopPropagation()} data-id="element-799">
        <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-800">
          <div className="flex justify-between items-start mb-4" data-id="element-801">
            <h3 className="text-xl font-bold" data-id="element-802">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors" data-id="element-803">
              <X className="w-6 h-6" data-id="element-804" />
            </button>
          </div>
          <div className="space-y-4" data-id="element-805">
            <textarea value={notes} onChange={handleNotesChange} className="w-full bg-white/5 text-white rounded-lg px-4 py-3 min-h-[200px] focus:ring-2 ring-purple-500/20" placeholder="Bu soru hakkındaki notlarınızı buraya ekleyin..." data-id="element-806" />
            {/* Image upload */}
            <div className="flex items-center justify-between" data-id="element-807">
              <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/jpeg,image/png" className="hidden" data-id="element-808" />
              <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors" data-id="element-809">
                <ImageIcon className="w-4 h-4" data-id="element-810" />
                Görsel Ekle
              </button>
              <button onClick={handleSaveNotes} disabled={!isDirty} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isDirty ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-811">
                <Save className="w-4 h-4" data-id="element-812" />
                Kaydet
              </button>
            </div>
            {/* Uploaded images */}
            {noteImages.length > 0 && <div className="mt-4 space-y-3" data-id="element-813">
                <h4 className="font-medium" data-id="element-814">Yüklenen Görseller</h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-815">
                  {noteImages.map((image, index) => <div key={index} className="relative group" data-id="element-816">
                      <img src={image} alt={`Not görseli ${index + 1}`} className="rounded-lg w-full object-cover aspect-video" data-id="element-817" />
                      <button onClick={() => handleRemoveImage(index)} className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-818">
                        <Trash2 className="w-4 h-4" data-id="element-819" />
                      </button>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>
      </div>
      <ToastContainer data-id="element-820" />
    </div>;
}